import { devLog } from '../../utils/devLog';
import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { useAppContext } from '../../hooks/useAppContext';
import { getCandidateScreenings } from '../../utils/api';
import Card from '../common/Card';

const InterviewResults = ({ applicant, jobId }) => {
  const { jobs } = useAppContext();
  const [screenings, setScreenings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [transcriptOpen, setTranscriptOpen] = useState(false);

  const selectedJob = jobs.find(job => job.id === jobId);
  const roleId = selectedJob?.roleId;
  const PASS_THRESHOLD = 6.0;

  useEffect(() => {
    const fetchScreenings = async () => {
      if (!applicant || !applicant.email || !roleId) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const result = await getCandidateScreenings(roleId, applicant.email);
        if (result.success) {
          setScreenings(result.data || []);
        } else {
          setError('Failed to load screening results');
        }
      } catch (err) {
        devLog.error('Error fetching screenings:', err);
        setError('Failed to load screening results');
      } finally {
        setIsLoading(false);
      }
    };
    fetchScreenings();
  }, [applicant, roleId]);

  const hasData = screenings.length > 0;
  const primaryResult = hasData ? screenings[0] : null;

  if (isLoading) {
    return (
      <Card>
        <div style={{ padding: '40px', textAlign: 'center', color: '#718096' }}>
          Loading interview results...
        </div>
      </Card>
    );
  }

  if (!applicant) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#718096' }}>
          <div style={{ fontSize: '4rem', marginBottom: '20px', opacity: 0.5 }}>👤</div>
          <h3 style={{ color: '#4a5568', marginBottom: '10px' }}>Select a candidate to view interview results</h3>
          <p>Pick a profile from CV Analysis to load screening feedback.</p>
        </div>
      </Card>
    );
  }

  if (!hasData) return null;

  const details = primaryResult.details || {};
  const analysis = primaryResult.analysis || {};
  const transcript = primaryResult.transcript || [];

  const technicalScore = details.technical || 0;
  const communicationScore = details.communication || 0;
  const overallScore = details.overall || 0;
  const overallScore10 = overallScore / 10;

  const isNotAttempted = primaryResult.result === 'Not Attempted';
  const isPassed = !isNotAttempted && overallScore10 >= PASS_THRESHOLD;
  
  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();

    // Sheet 1: Summary
    const summaryData = [
      ['Interview Evaluation Report'],
      [''],
      ['Candidate Name', applicant?.name || 'N/A'],
      ['Email', applicant?.email || 'N/A'],
      ['Job Position', selectedJob?.title || 'N/A'],
      ['Result', primaryResult.result || 'N/A'],
      ['Overall Score', `${overallScore}/100`],
      ['Score (out of 10)', overallScore10.toFixed(1)],
      ['Technical Score', `${technicalScore}/100`],
      ['Communication Score', `${communicationScore}/100`],
      ['Evaluated At', primaryResult.evaluatedAt || 'N/A'],
      ['Recommendation Reason', primaryResult.notes || 'N/A'],
      [''],
      ['Technical Analysis', analysis.technical_analysis || 'N/A'],
      ['Behavioral Analysis', analysis.behavioral_analysis || 'N/A'],
      [''],
      ['Strengths'],
      ...(analysis.strengths || []).map(s => ['', s]),
      [''],
      ['Weaknesses'],
      ...(analysis.weaknesses || []).map(w => ['', w]),
    ];
    const ws1 = XLSX.utils.aoa_to_sheet(summaryData);
    ws1['!cols'] = [{ wch: 25 }, { wch: 60 }];
    XLSX.utils.book_append_sheet(wb, ws1, 'Evaluation Summary');

    // Sheet 2: Transcript
    if (transcript.length > 0) {
      const transcriptData = [
        ['#', 'Question', 'Answer'],
        ...transcript.map(t => [t.question_number, t.question, t.answer])
      ];
      const ws2 = XLSX.utils.aoa_to_sheet(transcriptData);
      ws2['!cols'] = [{ wch: 5 }, { wch: 50 }, { wch: 60 }];
      XLSX.utils.book_append_sheet(wb, ws2, 'Interview Transcript');
    }

    const fileName = `${applicant?.name || 'candidate'}_interview_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const scoreColor = (s) => s >= 60 ? '#48bb78' : s >= 40 ? '#ed8936' : '#f56565';

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 className="card-title" style={{ margin: 0 }}>
          📊 Interview Screening Results
        </h3>
        {!isNotAttempted && (
          <button
            onClick={handleExportExcel}
            style={{
              background: '#1d6f42',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.85rem',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            📥 Export to Excel
          </button>
        )}
      </div>

      <div className="interview-results-grid">
        {/* Left Panel - Overall Score */}
        <div style={{
          background: '#f7fafc', padding: '30px', borderRadius: '15px',
          textAlign: 'center', display: 'flex', flexDirection: 'column',
          alignItems: 'center', height: 'fit-content'
        }}>
          <div style={{
            width: '160px', height: '160px', borderRadius: '50%',
            background: isNotAttempted
              ? '#e2e8f0'
              : `conic-gradient(${isPassed ? '#48bb78' : '#f56565'} ${(overallScore10 / 10) * 360}deg, #e2e8f0 0deg)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px', position: 'relative'
          }}>
            <div style={{
              width: '130px', height: '130px', borderRadius: '50%', background: 'white',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '3rem', fontWeight: 700,
              color: isNotAttempted ? '#a0aec0' : (isPassed ? '#48bb78' : '#f56565')
            }}>
              {isNotAttempted ? '–' : overallScore10.toFixed(1)}
            </div>
          </div>
          <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#2d3748', marginBottom: '5px' }}>
            {isNotAttempted ? 'Pending' : 'Overall Score'}
          </div>
          <div style={{ fontSize: '1rem', color: '#718096', marginBottom: '20px' }}>
            {isNotAttempted ? 'Interview not completed' : `out of 10 (${overallScore.toFixed(0)}%)`}
          </div>
          {!isNotAttempted && (
            <div style={{
              background: isPassed
                ? 'linear-gradient(135deg, #48bb78, #38a169)'
                : 'linear-gradient(135deg, #f56565, #e53e3e)',
              color: 'white', padding: '15px 30px', borderRadius: '8px',
              fontWeight: 700, fontSize: '1rem', width: '100%',
              textAlign: 'center', display: 'flex', alignItems: 'center',
              justifyContent: 'center', gap: '8px'
            }}>
              <span style={{ fontSize: '1.2rem' }}>{isPassed ? '✔' : '✗'}</span>
              {primaryResult.result || (isPassed ? 'APPROVED' : 'REJECTED')}
            </div>
          )}

          {/* Recommendation reason */}
          {primaryResult.notes && !isNotAttempted && (
            <div style={{
              marginTop: '15px', padding: '12px', background: 'white',
              borderRadius: '8px', fontSize: '0.82rem', color: '#4a5568',
              lineHeight: 1.5, border: '1px solid #e2e8f0', textAlign: 'left'
            }}>
              <strong style={{ color: '#2d3748' }}>Reason:</strong><br />
              {primaryResult.notes}
            </div>
          )}
        </div>

        {/* Right Panel */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {/* Technical Score */}
          <div style={{ background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h4 style={{ fontSize: '1rem', fontWeight: 700, color: '#4a5568', margin: 0 }}>⚙️ Technical Skills</h4>
              <span style={{ background: scoreColor(technicalScore), color: 'white', padding: '4px 12px', borderRadius: '20px', fontWeight: 700, fontSize: '0.85rem' }}>
                {technicalScore.toFixed(0)}/100
              </span>
            </div>
            <div style={{ width: '100%', height: '7px', background: '#e2e8f0', borderRadius: '4px', marginBottom: '10px' }}>
              <div style={{ width: `${technicalScore}%`, height: '100%', background: scoreColor(technicalScore), borderRadius: '4px', transition: 'width 0.5s ease' }} />
            </div>
            {analysis.technical_analysis && (
              <p style={{ color: '#718096', lineHeight: 1.5, margin: 0, fontSize: '0.88rem' }}>{analysis.technical_analysis}</p>
            )}
          </div>

          {/* Communication Score */}
          <div style={{ background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h4 style={{ fontSize: '1rem', fontWeight: 700, color: '#4a5568', margin: 0 }}>🗣️ Communication</h4>
              <span style={{ background: scoreColor(communicationScore), color: 'white', padding: '4px 12px', borderRadius: '20px', fontWeight: 700, fontSize: '0.85rem' }}>
                {communicationScore.toFixed(0)}/100
              </span>
            </div>
            <div style={{ width: '100%', height: '7px', background: '#e2e8f0', borderRadius: '4px', marginBottom: '10px' }}>
              <div style={{ width: `${communicationScore}%`, height: '100%', background: scoreColor(communicationScore), borderRadius: '4px', transition: 'width 0.5s ease' }} />
            </div>
            {analysis.behavioral_analysis && (
              <p style={{ color: '#718096', lineHeight: 1.5, margin: 0, fontSize: '0.88rem' }}>{analysis.behavioral_analysis}</p>
            )}
          </div>

          {/* Strengths & Weaknesses */}
          {(analysis.strengths?.length > 0 || analysis.weaknesses?.length > 0) && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {analysis.strengths?.length > 0 && (
                <div style={{ background: '#f0fff4', padding: '14px', borderRadius: '10px', border: '1px solid #c6f6d5' }}>
                  <div style={{ fontWeight: 700, color: '#276749', marginBottom: '8px', fontSize: '0.85rem' }}>✅ Strengths</div>
                  {analysis.strengths.map((s, i) => (
                    <div key={i} style={{ fontSize: '0.82rem', color: '#2f855a', marginBottom: '4px' }}>• {s}</div>
                  ))}
                </div>
              )}
              {analysis.weaknesses?.length > 0 && (
                <div style={{ background: '#fff5f5', padding: '14px', borderRadius: '10px', border: '1px solid #fed7d7' }}>
                  <div style={{ fontWeight: 700, color: '#9b2c2c', marginBottom: '8px', fontSize: '0.85rem' }}>⚠️ Weaknesses</div>
                  {analysis.weaknesses.map((w, i) => (
                    <div key={i} style={{ fontSize: '0.82rem', color: '#c53030', marginBottom: '4px' }}>• {w}</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Transcript Accordion */}
      {transcript.length > 0 && (
        <div style={{ marginTop: '24px' }}>
          <button
            onClick={() => setTranscriptOpen(!transcriptOpen)}
            style={{
              width: '100%', padding: '14px 20px', background: '#f8fafc',
              border: '2px solid #e2e8f0', borderRadius: '10px', cursor: 'pointer',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              fontWeight: 600, color: '#4a5568', fontSize: '0.95rem'
            }}
          >
            <span>💬 Interview Transcript ({transcript.length} questions)</span>
            <span style={{ fontSize: '0.8rem', color: '#667eea' }}>
              {transcriptOpen ? '▲ Hide' : '▼ Show'}
            </span>
          </button>

          {transcriptOpen && (
            <div style={{
              marginTop: '12px', border: '1px solid #e2e8f0',
              borderRadius: '10px', overflow: 'hidden'
            }}>
              {transcript.map((item, idx) => (
                <div key={idx} style={{
                  borderBottom: idx < transcript.length - 1 ? '1px solid #e2e8f0' : 'none',
                  padding: '16px 20px',
                  background: idx % 2 === 0 ? '#f8fafc' : 'white'
                }}>
                  <div style={{
                    fontSize: '0.78rem', fontWeight: 700, color: '#667eea',
                    textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px'
                  }}>
                    Q{item.question_number}
                  </div>
                  <div style={{
                    fontSize: '0.92rem', fontWeight: 600, color: '#2d3748',
                    marginBottom: '8px', lineHeight: 1.5
                  }}>
                    🤖 {item.question}
                  </div>
                  <div style={{
                    fontSize: '0.88rem', color: '#4a5568', lineHeight: 1.6,
                    paddingLeft: '12px', borderLeft: '3px solid #667eea'
                  }}>
                    👤 {item.answer}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default InterviewResults;